import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, MapPin, X, Check } from 'lucide-react';

const CalendarModal = ({ isOpen, onClose, customerId, customerName }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [meetingType, setMeetingType] = useState('video');
  const [location, setLocation] = useState('Zoom');
  const [title, setTitle] = useState(`Meeting with ${customerName}`);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const API_HOST = process.env.REACT_APP_API_URL || "50.17.67.70";

  // Generate available time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        slots.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Format date as YYYY-MM-DD
      const formattedDate = selectedDate.toISOString().split('T')[0];

      // Generate a unique meeting ID
      const meetingId = `MTG${Date.now().toString().slice(-6)}`;

      const meetingData = {
        advisor_id: "ADV245", // Default advisor ID
        created_at: new Date().toISOString(),
        customer_id: customerId,
        end_time: endTime,
        id: meetingId,
        location: location,
        meeting_date: formattedDate,
        meeting_type: meetingType,
        notes: notes,
        start_time: startTime,
        status: "scheduled",
        title: title
      };

      const response = await fetch(`http://${API_HOST}:5020/table/meetings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(meetingData)
      });

      if (!response.ok) {
        throw new Error(`Failed to schedule meeting: ${response.statusText}`);
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);

    } catch (err) {
      console.error("Error scheduling meeting:", err);
      setError(err.message || "Failed to schedule meeting. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000]">
      <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-[550px] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="m-0 text-xl text-gray-900">Schedule Meeting with {customerName}</h2>
          <button
            className="bg-transparent border-none cursor-pointer text-gray-500 p-1 rounded hover:bg-gray-100 hover:text-gray-900"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <Check size={40} className="text-success mb-4" />
            <p className="text-lg text-gray-900 m-0">Meeting successfully scheduled!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-4">
              <label htmlFor="title" className="block mb-1.5 font-medium text-gray-700 text-sm">Meeting Title</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
              />
            </div>

            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label htmlFor="date" className="block mb-1.5 font-medium text-gray-700 text-sm">Date</label>
                <input
                  type="date"
                  id="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange(new Date(e.target.value))}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                />
              </div>

              <div className="flex-1">
                <label htmlFor="meetingType" className="block mb-1.5 font-medium text-gray-700 text-sm">Meeting Type</label>
                <select
                  id="meetingType"
                  value={meetingType}
                  onChange={(e) => setMeetingType(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                >
                  <option value="video">Video Call</option>
                  <option value="phone">Phone Call</option>
                  <option value="in-person">In-Person</option>
                </select>
              </div>
            </div>

            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label htmlFor="startTime" className="block mb-1.5 font-medium text-gray-700 text-sm">Start Time</label>
                <select
                  id="startTime"
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    // Auto-set end time 1 hour later
                    const startIndex = timeSlots.indexOf(e.target.value);
                    if (startIndex >= 0 && startIndex + 2 < timeSlots.length) {
                      setEndTime(timeSlots[startIndex + 2]);
                    }
                  }}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                >
                  {timeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label htmlFor="endTime" className="block mb-1.5 font-medium text-gray-700 text-sm">End Time</label>
                <select
                  id="endTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                >
                  {timeSlots.map((time) => (
                    <option
                      key={time}
                      value={time}
                      disabled={time <= startTime}
                    >
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="location" className="block mb-1.5 font-medium text-gray-700 text-sm">Location</label>
              <input
                type="text"
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={meetingType === 'in-person' ? 'Address' : meetingType === 'video' ? 'Zoom/Teams Link' : 'Phone Number'}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="notes" className="block mb-1.5 font-medium text-gray-700 text-sm">Notes (Optional)</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="3"
                placeholder="Add any additional information about this meeting"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20 resize-y"
              />
            </div>

            {error && (
              <div className="text-error bg-red-100 p-3 rounded-md mt-4 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2.5 bg-white border border-gray-300 rounded-md font-medium text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-4 py-2.5 rounded-md font-medium text-sm text-white transition-colors ${isSubmitting
                    ? 'bg-secondaryLight border-secondaryLight cursor-not-allowed'
                    : 'bg-secondary border-secondary hover:bg-secondaryDark'
                  }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Scheduling...' : 'Schedule Meeting'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CalendarModal;